/**
 * One browser's side of the project chat: it shows its own actions at once (a message waits with a clock until the
 * relay echoes it back) and keeps the chat's state from the relay's events. Reactions, edits and read marks that
 * could not leave wait in an outbox and go again when the connection returns; a message that could not leave says
 * so and is sent again on request. The typing flag goes out while there is a draft and stops by itself.
 */
import { CHAT_TEXT_LIMIT, TYPING_IDLE_MS, TYPING_REFRESH_MS, isChatIncoming,
  type ChatConnection, type ChatIncoming, type ChatOutgoing, type ChatReaction, type ChatTransport } from './protocol';
import { addLocalMessage, applyChatEvents, compareMarks, dropLocalMessage, emptyChat, expireTyping, markOf, setLocalStatus, type ChatState, type ReadMark } from './state';
import type { TranslationKey } from '../../i18n/I18n';

export type ChatSnapshot = {
  readonly state: ChatState;
  readonly connection: ChatConnection;
  /** The history has arrived at least once: until then the thread shows its skeleton. */
  readonly ready: boolean;
  readonly self: { userId: string; displayName: string };
  /** Something of yours that did not reach the others yet, for the banner over the thread: the key of its text. */
  readonly error: TranslationKey | null;
};
export type ChatTimers = { set(run: () => void, ms: number): unknown; clear(handle: unknown): void };
const defaultTimers: ChatTimers = { set: (run, ms) => setTimeout(run, ms), clear: handle => clearTimeout(handle as ReturnType<typeof setTimeout>) };
const NOT_SENT: TranslationKey = '#ChatNotAllSent';

export class ChatSession {
  private snapshot: ChatSnapshot;
  private listeners = new Set<() => void>();
  private unsubscribe: (() => void) | null = null;
  private readonly timers: ChatTimers;
  /** Own messages the relay has not confirmed, to send again on request. */
  private unsent = new Map<string, ChatOutgoing>();
  /** Reactions, edits and read marks that could not leave, by what they change: only the latest goes again. */
  private outbox = new Map<string, ChatOutgoing>();
  /** The latest of them sent for each thing they change, so an older one failing late cannot outvote it. */
  private latest = new Map<string, ChatOutgoing>();
  private typingSentAt = -Infinity;
  private typingIdle: unknown = null;
  private typingExpiry: unknown = null;
  private readTimer: unknown = null;
  private readQueued: ReadMark | null = null;
  private disposed = false;

  constructor(private readonly options: { transport: ChatTransport; now?: () => number; timers?: ChatTimers }) {
    this.timers = options.timers ?? defaultTimers;
    this.snapshot = { state: emptyChat(), connection: 'connecting', ready: false, self: options.transport.self, error: null };
  }
  private now(): number { return (this.options.now ?? Date.now)(); }
  get = (): ChatSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  get self() { return this.snapshot.self; }

  start(): void {
    if (this.unsubscribe || this.disposed) return;
    this.unsubscribe = this.options.transport.subscribe(events => this.receive(events), status => this.status(status));
  }

  dispose(): void {
    if (this.disposed) return;
    if (this.typingSentAt > -Infinity) void this.options.transport.send([{ type: 'ChatTyping', data: { typing: false } }]).catch(() => {});
    this.disposed = true;
    for (const handle of [this.typingIdle, this.typingExpiry, this.readTimer]) if (handle !== null) this.timers.clear(handle);
    this.typingIdle = this.typingExpiry = this.readTimer = null;
    this.unsubscribe?.(); this.unsubscribe = null;
    this.options.transport.dispose();
    this.listeners.clear();
  }

  /** Sends a message; returns its id, or null when there is nothing to send. */
  send(text: string, replyTo: string | null = null): string | null {
    const body = text.trim();
    if (this.disposed || !body || body.length > CHAT_TEXT_LIMIT) return null;
    const state = this.snapshot.state, self = this.self;
    const reply = replyTo && state.messages.some(message => message.id === replyTo && !message.deleted && !message.local) ? replyTo : null;
    const id = crypto.randomUUID(), sentAt = this.now();
    const event: ChatOutgoing = { type: 'ChatMessage', key: id, data: { id, text: body, replyTo: reply, sentAt } };
    this.unsent.set(id, event);
    const stopTyping = this.stopTypingEvents();
    this.set({ state: addLocalMessage(state, { id, authorId: self.userId, authorName: self.displayName, text: body, replyTo: reply, sentAt, editedAt: null, deleted: false }, this.now()) });
    this.deliver(id, [event, ...stopTyping]);
    return id;
  }

  /** A message that could not leave, sent again. */
  retry(id: string): void {
    const event = this.unsent.get(id);
    if (!event || this.disposed) return;
    this.set({ state: setLocalStatus(this.snapshot.state, id, 'pending') });
    this.deliver(id, [event]);
  }

  /** A message that could not leave, taken back. */
  discard(id: string): void {
    this.unsent.delete(id);
    this.set({ state: dropLocalMessage(this.snapshot.state, id) });
  }

  private deliver(id: string, events: ChatOutgoing[]): void {
    this.options.transport.send(events).then(() => {
      // Handed to the relay: one tick, even before its echo gives the message its place.
      if (this.disposed) return;
      this.unsent.delete(id);
      this.set({ state: setLocalStatus(this.snapshot.state, id, undefined), error: this.outbox.size ? this.snapshot.error : null });
    }, () => {
      if (this.disposed || !this.unsent.has(id)) return;
      this.set({ state: setLocalStatus(this.snapshot.state, id, 'failed') });
    });
  }

  /** Your own message, rewritten; the others see «изменено». */
  edit(id: string, text: string): boolean {
    const message = this.ownMessage(id), body = text.trim();
    if (!message || !body || body.length > CHAT_TEXT_LIMIT || body === message.text) return false;
    this.publishOwn({ type: 'ChatMessage', key: id, data: { id, text: body, replyTo: message.replyTo, sentAt: message.sentAt, editedAt: Math.max(this.now(), (message.editedAt ?? 0) + 1) } }, `message:${id}`, message.at);
    return true;
  }

  /** Your own message, deleted for everyone; one that never left is simply taken back. */
  remove(id: string): void {
    const message = this.snapshot.state.messages.find(candidate => candidate.id === id);
    if (!message || message.authorId !== this.self.userId || message.deleted) return;
    if (message.local) { this.discard(id); return; }
    this.publishOwn({ type: 'ChatMessage', key: id, data: { id, text: '', replyTo: message.replyTo, sentAt: message.sentAt, editedAt: Math.max(this.now(), (message.editedAt ?? 0) + 1), deleted: true } }, `message:${id}`, message.at);
  }

  /**
   * Your reaction to a message: one at most, as in Instagram — another emoji replaces it, the same one again takes it
   * off, null takes off whatever is there.
   */
  react(id: string, emoji: ChatReaction | null): void {
    const state = this.snapshot.state, message = state.messages.find(candidate => candidate.id === id);
    if (!message || message.deleted || message.local || this.disposed) return;
    const current = state.reactions[id]?.[this.self.userId] ?? null;
    const next = emoji === current ? null : emoji;
    if (next === current) return;
    this.publishOwn({ type: 'ChatReaction', key: id, data: { messageId: id, emoji: next } }, `reaction:${id}`, this.now());
  }

  /** You have seen this message: your read mark moves up to it, and the others learn it a moment later. */
  markRead(id: string): void {
    const state = this.snapshot.state, message = state.messages.find(candidate => candidate.id === id);
    if (!message || message.local || this.disposed) return;
    const mark = markOf(message), own = state.reads[this.self.userId];
    if (own && compareMarks(own, mark) >= 0) return;
    this.set({ state: applyChatEvents(state, [this.stamp({ type: 'ChatRead', data: mark }, this.now())], this.now()) });
    // A scroll through the history marks many messages in a row; the relay hears the last of them.
    this.readQueued = mark;
    if (this.readTimer !== null) return;
    this.readTimer = this.timers.set(() => {
      this.readTimer = null;
      const queued = this.readQueued;
      this.readQueued = null;
      if (queued && !this.disposed) this.sendOther({ type: 'ChatRead', data: queued }, 'read');
    }, 250);
  }

  /** Whether you are typing: true on every change of a non-empty draft, false when it is sent, cleared or left. */
  setTyping(active: boolean): void {
    if (this.disposed) return;
    if (this.typingIdle !== null) { this.timers.clear(this.typingIdle); this.typingIdle = null; }
    if (!active) {
      const events = this.stopTypingEvents();
      if (events.length) void this.options.transport.send(events).catch(() => {});
      return;
    }
    const now = this.now();
    if (now - this.typingSentAt >= TYPING_REFRESH_MS) {
      this.typingSentAt = now;
      void this.options.transport.send([{ type: 'ChatTyping', data: { typing: true } }]).catch(() => {});
    }
    this.typingIdle = this.timers.set(() => { this.typingIdle = null; this.setTyping(false); }, TYPING_IDLE_MS);
  }

  /** Tries the connection again now; what waits in the outbox goes when it is back. */
  reconnect(): void { this.options.transport.reconnect?.(); }

  dismissError(): void { if (this.snapshot.error) this.set({ error: null }); }

  private stopTypingEvents(): ChatOutgoing[] {
    if (this.typingIdle !== null) { this.timers.clear(this.typingIdle); this.typingIdle = null; }
    if (this.typingSentAt === -Infinity) return [];
    this.typingSentAt = -Infinity;
    return [{ type: 'ChatTyping', data: { typing: false } }];
  }

  private ownMessage(id: string) {
    const message = this.snapshot.state.messages.find(candidate => candidate.id === id);
    return message && message.authorId === this.self.userId && !message.deleted && !message.local && !this.disposed ? message : null;
  }

  private stamp(event: ChatOutgoing, at: number): ChatIncoming {
    return { ...event, userId: this.self.userId, displayName: this.self.displayName, at } as ChatIncoming;
  }

  /** An own change shown at once, as if the relay had already echoed it, then sent. */
  private publishOwn(event: ChatOutgoing, key: string, at: number): void {
    this.set({ state: applyChatEvents(this.snapshot.state, [this.stamp(event, at)], this.now()) });
    this.sendOther(event, key);
  }

  private sendOther(event: ChatOutgoing, key: string): void {
    this.outbox.delete(key);
    this.latest.set(key, event);
    this.options.transport.send([event]).then(() => {
      if (this.latest.get(key) === event) this.latest.delete(key);
      if (!this.disposed && !this.outbox.size && this.snapshot.error === NOT_SENT) this.set({ error: null });
    }, () => {
      // Only the latest news about a thing waits: a newer reaction state, edit or read mark decides for itself.
      if (this.disposed || this.latest.get(key) !== event) return;
      this.outbox.set(key, event);
      this.set({ error: NOT_SENT });
    });
  }

  private flushOutbox(): void {
    const waiting = [...this.outbox.entries()];
    this.outbox.clear();
    for (const [key, event] of waiting) this.sendOther(event, key);
    if (!waiting.length && this.snapshot.error === NOT_SENT) this.set({ error: null });
  }

  private receive(events: ChatIncoming[]): void {
    if (this.disposed) return;
    const valid = events.filter(isChatIncoming);
    if (!valid.length) return;
    for (const event of valid) if (event.type === 'ChatMessage' && event.userId === this.self.userId) this.unsent.delete(event.data.id);
    const state = applyChatEvents(this.snapshot.state, valid, this.now());
    if (state !== this.snapshot.state) this.set({ state });
  }

  private status(connection: ChatConnection): void {
    if (this.disposed) return;
    this.set({ connection, ready: this.snapshot.ready || connection === 'connected' });
    if (connection === 'connected') this.flushOutbox();
  }

  private set(patch: Partial<ChatSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    if (Object.keys(patch).every(key => next[key as keyof ChatSnapshot] === this.snapshot[key as keyof ChatSnapshot])) return;
    this.snapshot = next;
    this.scheduleExpiry();
    for (const listener of this.listeners) { try { listener(); } catch { /* a listener does not own delivery */ } }
  }

  /** The next typing flag to run out takes itself away on time. */
  private scheduleExpiry(): void {
    if (this.typingExpiry !== null) { this.timers.clear(this.typingExpiry); this.typingExpiry = null; }
    const until = Object.values(this.snapshot.state.typing);
    if (!until.length || this.disposed) return;
    const next = Math.min(...until);
    this.typingExpiry = this.timers.set(() => {
      this.typingExpiry = null;
      const state = expireTyping(this.snapshot.state, this.now());
      if (state !== this.snapshot.state) this.set({ state }); else this.scheduleExpiry();
    }, Math.max(0, next - this.now()) + 20);
  }
}
